import { FilterButton } from './FilterButton';
import { Box, Button, Checkbox, List, ListItem, ListItemText, Popover } from '@mui/material';
import React, { ReactNode, useState } from 'react';

export type OptionType = {
  title: string;
  name: string;
  selected: boolean;
};

type Props = {
  id: string;
  title: ReactNode | string;
  options: OptionType[];
  onAllOptionsSelect: () => void;
  onOptionSelect: (selectedOption: OptionType) => void;
  onOptionsClear: () => void;
  onOpen: () => void;
};

export function FilterField(props: Props) {
  const { id, title, options, onOpen, onOptionSelect, onAllOptionsSelect, onOptionsClear } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const selectedOptions = options.filter((option) => option.selected);
  const isAllSelected = selectedOptions.length === options.length;
  const areSomeOptionsChecked =
    selectedOptions.length > 0 && selectedOptions.length < options.length;

  function handleButtonClick(event: React.MouseEvent<HTMLButtonElement>) {
    setAnchorEl(event.currentTarget);
    onOpen();
  }

  function handleClose() {
    setAnchorEl(null);
  }

  function handleSelectAll() {
    if (isAllSelected) {
      onOptionsClear();

      return;
    }

    onAllOptionsSelect();
  }

  function handleOptionToggle(selectedOption: OptionType) {
    onOptionSelect(selectedOption);
  }

  return (
    <>
      <FilterButton
        id={id}
        anchorEl={anchorEl}
        onClick={handleButtonClick}
        numberOfSelectedOptions={selectedOptions.length}
      >
        {title}
      </FilterButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        sx={{
          boxShadow: '4px 16px 32px 0px rgba(29, 29, 29, 0.08)',
          borderRadius: '4px',
        }}
      >
        <Box data-testid={`${id}-menu`}>
          <List
            sx={{
              maxHeight: 350,
              minHeight: 200,
              maxWidth: 528,
              minWidth: 400,
              overflowY: 'auto',
              pb: 0,
              cursor: 'pointer',
            }}
          >
            <ListItem
              sx={{ p: 0, pr: 2 }}
              data-testid="select-all-filter-menu-item"
              onClick={handleSelectAll}
            >
              <Checkbox
                checked={isAllSelected}
                indeterminate={areSomeOptionsChecked}
                data-testid={`${id}_checkbox_all`}
              />
              <ListItemText primary="Select All" data-testid={`${id}_all`} sx={{ my: 1, mx: 0 }} />
            </ListItem>
            <Box>
              <Box>
                {options.map((option) => {
                  const checked = selectedOptions.some((selected) => selected.name === option.name);

                  return (
                    <ListItem
                      sx={{ p: 0, pr: 2 }}
                      key={option.name}
                      data-testid="filter-menu-item"
                      data-checked={checked}
                      onClick={() => handleOptionToggle(option)}
                    >
                      <Checkbox checked={checked} data-testid={`${id}-checkbox`} />
                      <ListItemText
                        primary={option.title}
                        data-testid={`${id}-item`}
                        sx={{ my: 1, mx: 0 }}
                      />
                    </ListItem>
                  );
                })}
              </Box>
            </Box>
          </List>
          <Button data-testid={`${id}_clear`} variant="text" onClick={onOptionsClear}>
            Clear all
          </Button>
        </Box>
      </Popover>
    </>
  );
}
